// useAeroAi — WebSocket-backed chat hook for the Aero AI service.
//
// Maintains a message history, streams tokens from the server into the
// current assistant message, and lets the caller attach live context
// (current vision analysis + sensors) to each ask so replies are grounded
// in the rider's actual numbers.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AeroAiMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  streaming?: boolean;
  createdAt: number;
}

export interface AeroAiContext {
  cda?: number;
  cd?: number;
  frontal_area_m2?: number;
  drag_power_w?: number;
  drag_force_n?: number;
  position_class?: string;
  calibration_source?: string;
  calibration_confidence?: number;
  predicted_speed_kph?: number;
  watts_saved?: number;
  segments?: {
    head_pct: number;
    torso_pct: number;
    arms_pct: number;
    legs_pct: number;
  };
}

export interface AeroAiSensors {
  power_w?: number;
  cadence_rpm?: number;
  heart_rate_bpm?: number;
  speed_mps?: number;
}

interface Options {
  wsUrl: string;
  userId?: string;
  sessionId?: string;
  /** Called each tick to fetch latest vision analysis to attach to the next ask. */
  getContext?: () => AeroAiContext | undefined;
  /** Called each tick to fetch latest sensor data. */
  getSensors?: () => AeroAiSensors | undefined;
  enabled?: boolean;
}

interface State {
  messages: AeroAiMessage[];
  /** Assistant-only observations, oldest → newest. Use this to render the feed. */
  observations: AeroAiMessage[];
  isStreaming: boolean;
  connected: boolean;
  error: string | null;
  ask: (text: string) => void;
  clear: () => void;
}

export function useAeroAi(opts: Options): State {
  const { wsUrl, userId = '', sessionId = '', enabled = true } = opts;

  const [messages, setMessages] = useState<AeroAiMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const currentAssistantIdRef = useRef<string | null>(null);
  // We hold callbacks in refs so the WS handler always sees the latest closures
  // without having to recreate the WebSocket.
  const getContextRef = useRef(opts.getContext);
  const getSensorsRef = useRef(opts.getSensors);
  useEffect(() => {
    getContextRef.current = opts.getContext;
    getSensorsRef.current = opts.getSensors;
  }, [opts.getContext, opts.getSensors]);

  // Connect / disconnect lifecycle
  useEffect(() => {
    if (!enabled) return;

    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setError(null);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'token') {
            appendToken(msg.content as string);
          } else if (msg.type === 'done') {
            finalizeStream();
          } else if (msg.type === 'error') {
            setError(msg.content || 'Aero AI error');
            finalizeStream();
          }
        } catch (e) {
          console.error('Failed to parse Aero AI message', e);
        }
      };

      ws.onerror = () => {
        setError('Connection error');
      };

      ws.onclose = () => {
        setConnected(false);
        if (!closed) {
          // Auto-reconnect after a short delay
          reconnectTimer = setTimeout(connect, 2000);
        }
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled, wsUrl]);

  const appendToken = useCallback((token: string) => {
    setMessages((prev) => {
      const id = currentAssistantIdRef.current;
      if (!id) return prev;
      return prev.map((m) =>
        m.id === id ? { ...m, content: m.content + token } : m
      );
    });
  }, []);

  const finalizeStream = useCallback(() => {
    setMessages((prev) => {
      const id = currentAssistantIdRef.current;
      if (!id) return prev;
      return prev.map((m) =>
        m.id === id ? { ...m, streaming: false } : m
      );
    });
    currentAssistantIdRef.current = null;
    setIsStreaming(false);
  }, []);

  const ask = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setError('Aero AI is not connected yet.');
        return;
      }

      const now = Date.now();
      const userMsg: AeroAiMessage = {
        id: `u-${now}`,
        role: 'user',
        content: trimmed,
        createdAt: now,
      };
      const asstId = `a-${now}`;
      const asstMsg: AeroAiMessage = {
        id: asstId,
        role: 'assistant',
        content: '',
        streaming: true,
        createdAt: now + 1,
      };
      currentAssistantIdRef.current = asstId;
      setMessages((prev) => [...prev, userMsg, asstMsg]);
      setIsStreaming(true);
      setError(null);

      ws.send(
        JSON.stringify({
          type: 'ask',
          msg: trimmed,
          user_id: userId,
          session_id: sessionId,
          context: getContextRef.current?.(),
          sensors: getSensorsRef.current?.(),
        })
      );
    },
    [userId, sessionId]
  );

  const clear = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const observations = messages.filter(
    (m) => m.role === 'assistant' && (m.content.length > 0 || m.streaming)
  );

  return { messages, observations, isStreaming, connected, error, ask, clear };
}
